import styled, { keyframes, css } from "styled-components";
import { useRanger, Ranger } from "@tanstack/react-ranger";
import { useState, useEffect, useRef } from "react";

interface RangeInputProps {
  min: number;
  max: number;
  value: number[];
  tick: number;
  subTick: number;
  step: number;
  options: { asset?: string; label: string; value: number }[];
  onChange(value: number[]): void;
  onDrag?: (value: number[]) => void;
  formatPin(value: number): string;
  isAnswered?: boolean;
  showHint?: boolean;
  ariaNoValueText?: string;
  onFirstInteract?: () => void;
  savedFlash?: boolean;      // shows ✓ on the handle
  // confettiFlash?: boolean;   // NEW: triggers confetti only
}

const EDGE_INSET_PX = 6;

function RangeInput({
  step,
  value,
  options,
  onChange,
  onDrag,
  min,
  max,
  tick: initTick,
  subTick: initSubTick,
  formatPin = (v) => v.toString(),
  isAnswered = true,
  showHint = false,
  ariaNoValueText = "No value selected. Slide to choose.",
  onFirstInteract,
  savedFlash = false,
  // confettiFlash = false, // NEW
}: RangeInputProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const [internalValue, setInternalValue] = useState<ReadonlyArray<number>>(value);

  // dragging state
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const hasInteractedRef = useRef(false);

  // ✓ flash state
  const [showSavedFlash, setShowSavedFlash] = useState(false);
  useEffect(() => {
    if (savedFlash) {
      setShowSavedFlash(true);
      const t = setTimeout(() => setShowSavedFlash(false), 900);
      return () => clearTimeout(t);
    }
  }, [savedFlash]);

  // keep internal value in sync when not dragging
  useEffect(() => {
    if (!draggingRef.current) setInternalValue(value);
  }, [value]);

  // ranger
  const ranger = useRanger<HTMLDivElement>({
    getRangerElement: () => railRef.current,
    values: internalValue,
    min,
    max,
    stepSize: step,
    onChange: (instance: Ranger<HTMLDivElement>) => {
      draggingRef.current = false;
      setDragging(false);
      const vals = instance.sortedValues;
      setInternalValue(vals);
      onChange([...vals]);
    },
    onDrag: (instance: Ranger<HTMLDivElement>) => {
      if (!draggingRef.current) {
        draggingRef.current = true;
        setDragging(true);
        // cancel ✓ and confetti on new drag
        setShowSavedFlash(false);
        // setConfettiPieces([]);
      }
      const vals = instance.sortedValues;
      setInternalValue(vals);
      onDrag?.([...vals]);
    },
  });

  const steps = ranger.getSteps();
  const handles = ranger.handles();

  // ticks
  const majorTicks: number[] =
    initTick > 0
      ? (() => {
          const count = Math.max(2, initTick);
          const interval = (max - min) / (count - 1);
          return Array.from({ length: count }, (_, i) => min + i * interval);
        })()
      : [];

  const subTicks: number[] =
    initSubTick > 0 && majorTicks.length >= 2
      ? (() => {
          const subs: number[] = [];
          for (let i = 0; i < majorTicks.length - 1; i++) {
            const start = majorTicks[i];
            const end = majorTicks[i + 1];
            const delta = (end - start) / (initSubTick + 1);
            for (let j = 1; j <= initSubTick; j++) subs.push(start + j * delta);
          }
          return subs;
        })()
      : [];

  // filled logic
  const sortedVals = [...internalValue].sort((a, b) => a - b);
  const filledIntervals: Array<[number, number]> = [];
  if (sortedVals.length) {
    let start = min;
    for (let i = 0; i <= sortedVals.length; i++) {
      const end = i < sortedVals.length ? sortedVals[i] : max;
      if (i % 2 === 0) filledIntervals.push([start, end]);
      start = end;
    }
  }

  const showFill = isAnswered || dragging;
  const isValueFilled = (v: number) =>
    showFill && filledIntervals.some(([a, b]) => v >= a && v <= b);

  const hasValue = internalValue.length > 0;
  const maxVal = hasValue ? Math.max(...internalValue) : null;
  const leftEdgeFilled = showFill && hasValue;
  const rightEdgeFilled =
    showFill && hasValue && ((internalValue.length % 2 === 0) || maxVal === max);

  function formatNumberForTicks(v: number): string {
    return Number.isInteger(v) ? v.toString() : v.toFixed(1);
  }

  const markInteracted = () => {
    if (!hasInteractedRef.current) {
      hasInteractedRef.current = true;
      onFirstInteract?.();
    }
    setShowSavedFlash(false);
    // setConfettiPieces([]);
  };

  // ---------- Confetti (dependency-free) ----------
  type Piece = {
    id: number;
    dx: number;
    dy: number;
    rot: number;
    delay: number;
    dur: number;
    bg: string;
    w: number;
    h: number;
    br: number;
  };

  // const [confettiPieces, setConfettiPieces] = useState<Piece[]>([]);

  const CONFETTI_COLORS = ["#FFD166", "#06D6A0", "#EF476F", "#118AB2", "#8338EC", "#FFBE0B"];

  // 🔔 Fire confetti ONLY when parent toggles confettiFlash (not when savedFlash shows ✓)
  // useEffect(() => {
  //   if (!confettiFlash) return;

  //   // Tweakables
  //   const COUNT = 20;
  //   const DELAY_MAX = 300;
  //   const DUR_MIN = 900;
  //   const DUR_MAX = 1400;

  //   const rand = (a: number, b: number) => Math.random() * (b - a) + a;

  //   const pieces: Piece[] = Array.from({ length: COUNT }, (_, i) => ({
  //     id: i,
  //     dx: rand(-140, 140),
  //     dy: rand(-220, -120),
  //     rot: rand(-360, 360),
  //     delay: Math.round(rand(0, DELAY_MAX)),
  //     dur: Math.round(rand(DUR_MIN, DUR_MAX)),
  //     bg: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  //     w: Math.round(rand(5, 9)),
  //     h: Math.round(rand(8, 14)),
  //     br: Math.random() < 0.35 ? 999 : 2,
  //   }));
  //   setConfettiPieces(pieces);

  //   const killAt = Math.max(...pieces.map((p) => p.delay + p.dur)) + 150;
  //   const t = setTimeout(() => setConfettiPieces([]), killAt);
  //   return () => clearTimeout(t);
  // }, [confettiFlash]);

  // // left position of the current handle for the confetti container
  // const confettiLeftPct = ranger.getPercentageForValue(internalValue[0] ?? min);

  return (
    <Shell>
      <Rail ref={railRef} $inset={EDGE_INSET_PX}>
        <Bar />

        {leftEdgeFilled ? <EdgeFillCapLeft /> : <EdgeUnfilledCapLeft />}
        {rightEdgeFilled ? <EdgeFillCapRight /> : <EdgeUnfilledCapRight />}

        {/* Filled/empty segments */}
        {steps.map(({ left, width }, i) => (
          <Segment
            key={i}
            style={{ left: `${left}%`, width: `${width}%` }}
            $filled={showFill && i === 0}
          />
        ))}

        {/* Ticks */}
        {initTick > 0 &&
          majorTicks.map((v, i) => (
            <Tick key={`tick-${i}`} style={{ left: `${ranger.getPercentageForValue(v)}%` }}>
              <TickLabel>{formatNumberForTicks(v)}</TickLabel>
            </Tick>
          ))}
        {initSubTick > 0 &&
          subTicks.map((v, i) => (
            <SubTick key={`subtick-${i}`} style={{ left: `${ranger.getPercentageForValue(v)}%` }} />
          ))}

        {/* Dots & labels */}
        {options.map((opt, index) => {
          const left = `${ranger.getPercentageForValue(opt.value)}%`;
          const filled = isValueFilled(opt.value);
          return <OptionDotOnTrack key={`dot-${index}`} style={{ left }} $filled={filled} />;
        })}
        {options.map((opt, index) => {
          const percentage = ranger.getPercentageForValue(opt.value);
          const isEdge = index === 0 || index === options.length - 1;
          return (
            <OptionTick key={`label-${index}`} style={{ left: `${percentage}%` }}>
              <OptionLabel isEdge={isEdge}>{opt.label}</OptionLabel>
            </OptionTick>
          );
        })}

        {/* Handle */}
        {handles.map(
          ({ value, onKeyDownHandler, onMouseDownHandler, onTouchStart, isActive }, i) => (
            <HandleButton
              key={i}
              onKeyDown={(e) => {
                markInteracted();
                onKeyDownHandler(e);
              }}
              onMouseDown={(e) => {
                markInteracted();
                onMouseDownHandler(e);
              }}
              onTouchStart={(e) => {
                markInteracted();
                onTouchStart(e);
              }}
              role="slider"
              aria-label="slider"
              aria-valuemin={ranger.options.min}
              aria-valuemax={ranger.options.max}
              aria-valuenow={showFill ? value : undefined}
              aria-valuetext={showFill ? formatPin(value) : ariaNoValueText}
              $left={`${ranger.getPercentageForValue(value)}%`}
              $hintActive={!isAnswered && showHint && !isActive && !dragging && !showSavedFlash}
            >
              <HandleOuter
                $active={isActive}
                $unanswered={!isAnswered}
                $hintActive={!isAnswered && showHint && !isActive && !dragging && !showSavedFlash}
              >
                <HandleInner $savedFlash={showSavedFlash}>
                  {showSavedFlash ? "✓" : showFill ? formatPin(value) : "< >"}
                </HandleInner>
              </HandleOuter>
            </HandleButton>
          )
        )}

        {/* Confetti burst anchored to handle */}
        {/* {confettiPieces.length > 0 && (
          <ConfettiContainer $left={`${confettiLeftPct}%`}>
            {confettiPieces.map((p) => (
              <ConfettiPiece
                key={p.id}
                style={
                  {
                    "--dx": `${p.dx}px`,
                    "--dy": `${p.dy}px`,
                    "--rot": `${p.rot}deg`,
                    "--delay": `${p.delay}ms`,
                    "--dur": `${p.dur}ms`,
                    background: p.bg,
                    width: `${p.w}px`,
                    height: `${p.h}px`,
                    borderRadius: `${p.br}px`,
                  } as React.CSSProperties
                }
              />
            ))}
          </ConfettiContainer>
        )} */}
      </Rail>
    </Shell>
  );
}

/* 💅 Styles */

const nudgeX = keyframes`
  0%   { margin-left: -10px; }
  50%  { margin-left:  10px; }
  100% { margin-left: -10px; }
`;

const pulseRing = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0,0,0,0.25); }
  70%  { box-shadow: 0 0 0 12px rgba(0,0,0,0); }
  100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
`;

/* Save pop (inner only) */
const savePop = keyframes`
  0%   { transform: translate3d(0,0,0) scale(1); }
  45%  { transform: translate3d(0,-2px,0) scale(1.10); }
  100% { transform: translate3d(0,0,0) scale(1); }
`;

/* Confetti keyframes */
// const confettiBurst = keyframes`
//   0%   { transform: translate3d(0,0,0) rotate(0deg); opacity: 1; }
//   100% { transform: translate3d(var(--dx, 0), var(--dy, -100px), 0) rotate(var(--rot, 0deg)); opacity: 0; }
// `;

const Shell = styled.div`
  --track-h: 14px;
  --tick-h: 10px;
  --subtick-h: 6px;
  --label-gap: 16px;

  --dot-d: 4px;
  --bar-bleed: calc(var(--dot-d) * 2);

  --unfilled-overlay: rgba(0, 0, 0, 0);

  position: relative;
  width: 80%;
  margin: 1rem auto 3rem;
`;

const Rail = styled.div<{ $inset: number }>`
  position: relative;
  height: var(--track-h);
  margin-left: ${(p) => p.$inset}px;
  margin-right: ${(p) => p.$inset}px;
`;

const Bar = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(-1 * var(--bar-bleed));
  right: calc(-1 * var(--bar-bleed));
  background: #e0e0e0;
  border-radius: calc(var(--track-h) / 2);
  z-index: 0;
`;

const EdgeCapBase = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: var(--bar-bleed);
  z-index: 1;
  border-radius: 0;
`;
const EdgeFillCapLeft = styled(EdgeCapBase)`
  left: calc(-1 * var(--bar-bleed));
  background: ${(p) => p.theme.primary.background};
  border-top-left-radius: calc(var(--track-h) / 2);
  border-bottom-left-radius: calc(var(--track-h) / 2);
`;
const EdgeUnfilledCapLeft = styled(EdgeCapBase)`
  left: calc(-1 * var(--bar-bleed));
  background: var(--unfilled-overlay);
  border-top-left-radius: calc(var(--track-h) / 2);
  border-bottom-left-radius: calc(var(--track-h) / 2);
`;
const EdgeFillCapRight = styled(EdgeCapBase)`
  right: calc(-1 * var(--bar-bleed));
  background: ${(p) => p.theme.primary.background};
  border-top-right-radius: calc(var(--track-h) / 2);
  border-bottom-right-radius: calc(var(--track-h) / 2);
`;
const EdgeUnfilledCapRight = styled(EdgeCapBase)`
  right: calc(-1 * var(--bar-bleed));
  background: var(--unfilled-overlay);
  border-top-right-radius: calc(var(--track-h) / 2);
  border-bottom-right-radius: calc(var(--track-h) / 2);
`;

const Segment = styled.div<{ $filled: boolean }>`
  position: absolute;
  top: 0;
  height: 100%;
  background: ${({ $filled, theme }) =>
    $filled ? theme.primary.background : "var(--unfilled-overlay)"};
  z-index: 1;
`;

const Tick = styled.div`
  position: absolute;
  top: calc(-1 * (var(--tick-h) + 4px));
  width: 1px;
  height: var(--tick-h);
  background: #777;
  transform: translateX(-50%);
  z-index: 2;
`;

const TickLabel = styled.div`
  position: absolute;
  top: -22px;
  font-size: 0.7rem;
  color: #555;
  transform: translateX(-50%);
  white-space: nowrap;
`;

const SubTick = styled.div`
  position: absolute;
  top: calc(-1 * (var(--subtick-h) + 2px));
  width: 1px;
  height: var(--subtick-h);
  background: #bbb;
  transform: translateX(-50%);
  z-index: 2;
`;

const OptionTick = styled.div`
  position: absolute;
  top: calc(100% + var(--label-gap));
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
`;

const OptionLabel = styled.div<{ isEdge: boolean }>`
  font-size: 0.9rem;
  color: ${(props) => props.theme.primary.main};
  text-align: center;
  max-width: 70px;
  white-space: normal;
  line-height: 1.2;
`;

const OptionDotOnTrack = styled.div<{ $filled: boolean }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: var(--dot-d);
  height: var(--dot-d);
  border-radius: 50%;
  background: #66666684;
  pointer-events: none;
  z-index: 3;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${(props) => props.theme.primary.background};
    opacity: ${(props) => (props.$filled ? 0.28 : 0)};
    mix-blend-mode: multiply;
    pointer-events: none;
  }
`;

const HandleButton = styled.button<{ $left: string; $hintActive: boolean }>`
  position: absolute;
  top: 50%;
  left: ${(props) => props.$left};
  transform: translate(-50%, -50%);
  background: transparent;
  border: none;
  appearance: none;
  outline: none;
  z-index: 4;

  ${(p) =>
    p.$hintActive &&
    css`
      animation: ${nudgeX} 2.6s ease-in-out infinite;
    `}
`;

/* OUTER bubble: base lift/scale */
const HandleOuter = styled.div<{
  $active: boolean;
  $unanswered: boolean;
  $hintActive: boolean;
}>`
  background: ${(props) => props.theme.primary.background};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  color: ${(props) => props.theme.primary.contrast};
  font-weight: ${(props) => (props.$active ? "bold" : "normal")};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);

  transform: ${(props) =>
    props.$active ? "translateY(-130%) scale(1.3)" : "translateY(0) scale(0.9)"};
  transition: transform 0.30s ease-out;
  will-change: transform;
  transform-origin: center;

  ${(p) =>
    p.$unanswered &&
    p.$hintActive &&
    css`
      animation: ${pulseRing} 2.8s ease-out infinite;
    `}
`;

/* INNER content: save pop */
const HandleInner = styled.div<{ $savedFlash: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  width: 100%;
  height: 100%;
  transform: translateZ(0) scale(1);
  transform-origin: center;

  ${(p) =>
    p.$savedFlash &&
    css`
      animation: ${savePop} 280ms cubic-bezier(.22,.61,.36,1);
      will-change: transform;
    `}
`;

/* Confetti container anchored at the handle */
// const ConfettiContainer = styled.div<{ $left: string }>`
//   position: absolute;
//   top: 50%;
//   left: ${(p) => p.$left};
//   transform: translate(-50%, -50%);
//   width: 0;
//   height: 0;
//   pointer-events: none;
//   z-index: 6;
// `;

// /* A single confetti piece (uses CSS variables set inline) */
// const ConfettiPiece = styled.span`
//   position: absolute;
//   left: 0;
//   top: 0;
//   display: block;
//   opacity: 1;
//   will-change: transform, opacity;
//   animation: ${confettiBurst} var(--dur, 480ms) cubic-bezier(.2,.8,.3,1)
//     var(--delay, 0ms) forwards;
// `;

export default RangeInput;
